import { useTranslation } from 'react-i18next';

import InsightsRoundedIcon from '@mui/icons-material/InsightsRounded';
import { Card, CardContent, Divider, Stack, Typography } from '@mui/material';

import { EmptyState, ErrorState, LoadingState } from '@/shared/ui';

import { usePromotionStats } from '../../hooks';
import { formatDateTime, formatNumber, formatPrice } from '../../lib/format';

export interface PromotionStatsPanelProps {
  promotionId: string;
}

/**
 * Read-only "Statistics" card on the promotion detail page (`PromotionBuilderPage`) — redemption
 * count, discount spend and attributed revenue (epic-pricing-002 `GET .../promotions/{id}/stats`,
 * deferred in Phase 2b per plan-011 §1/§8.3, delivered here). Derived entirely from order
 * attribution events aggregated server-side; this panel only reads, never writes.
 *
 * State handling deliberately differs from `BudgetBurnBar`'s "always render the bar, just caption
 * it at 0%" idiom: a budget's consumed/cap ratio is meaningful even at zero (progress toward a
 * cap), but three independent absolute counts reading zero together look like a rendering bug to
 * an unfamiliar merchant rather than "no data yet" — so a genuinely never-redeemed promotion renders
 * the shared `EmptyState` instead of three zeroed tiles. `GET .../stats` never 404s for a
 * never-redeemed promotion (only a non-existent promotion 404s, surfaced via `ErrorState`).
 */
export function PromotionStatsPanel({ promotionId }: PromotionStatsPanelProps) {
  const { t } = useTranslation('promotions');
  const stats = usePromotionStats(promotionId);

  const hasAnyActivity = Boolean(
    stats.data && (stats.data.redemptionCount > 0 || stats.data.discountAmount > 0 || stats.data.attributedRevenue > 0),
  );

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
          <InsightsRoundedIcon fontSize="small" color="action" />
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {t('stats.title')}
          </Typography>
        </Stack>

        {stats.isLoading ? (
          <LoadingState label={t('stats.loading')} fullHeight={false} />
        ) : stats.isError ? (
          <ErrorState
            title={t('stats.loadError.title')}
            message={stats.error?.message ?? t('common:error.unknown')}
            onRetry={() => void stats.refetch()}
          />
        ) : !hasAnyActivity ? (
          <EmptyState
            icon={<InsightsRoundedIcon sx={{ fontSize: 40 }} />}
            title={t('stats.empty.title')}
            description={t('stats.empty.description')}
          />
        ) : (
          <Stack spacing={1.5}>
            <Stack direction="row" spacing={2}>
              <Stack sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="h6" noWrap sx={{ fontFamily: 'JetBrains Mono, monospace' }}>
                  {formatNumber(stats.data?.redemptionCount ?? 0)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('stats.redemptions')}
                </Typography>
              </Stack>
              <Divider orientation="vertical" flexItem />
              <Stack sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="h6" noWrap sx={{ fontFamily: 'JetBrains Mono, monospace' }}>
                  {formatPrice(stats.data?.discountAmount ?? 0)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('stats.discountSpend')}
                </Typography>
              </Stack>
              <Divider orientation="vertical" flexItem />
              <Stack sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="h6" noWrap sx={{ fontFamily: 'JetBrains Mono, monospace' }}>
                  {formatPrice(stats.data?.attributedRevenue ?? 0)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('stats.attributedRevenue')}
                </Typography>
              </Stack>
            </Stack>

            {stats.data?.lastRedeemedAt ? (
              <Typography variant="caption" color="text.secondary">
                {t('stats.lastRedeemedAt', { date: formatDateTime(stats.data.lastRedeemedAt) })}
              </Typography>
            ) : null}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}
